import { create } from 'zustand';

/*
    Connection health, reported by the JMAP client and rendered by the banner.

    This exists because of a specific worry Haktan raised: the mail server is
    stopped for a few seconds every night for the backup, and "if I check my mail
    in 2 years at 4 AM and don't see any mail, I will panic." A generic
    "can't connect" would fail that test, so the banner distinguishes the planned
    window from a real outage — and says so in words that stop the panic.
*/

/** 04:00 Europe/Istanbul, matching haktan-mail-backup.timer (01:00 UTC). */
const BACKUP_HOUR = 4;
const BACKUP_MINUTES = 6;

export function inBackupWindow(now = new Date()): boolean {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Istanbul',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(now);
    const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? -1);
    const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
    return hour === BACKUP_HOUR && minute < BACKUP_MINUTES;
}

interface NetState {
    online: boolean;
    /** When the outage started, so the banner can say how long it has been. */
    since: number | null;
    lastError: string | null;
    /** True when the outage began inside the nightly backup window. */
    planned: boolean;
    markDown: (reason: string) => void;
    markUp: () => void;
}

export const useNet = create<NetState>((set, get) => ({
    online: true,
    since: null,
    lastError: null,
    planned: false,

    markDown: (reason) => {
        if (!get().online) return;
        set({
            online: false,
            since: Date.now(),
            lastError: reason,
            // Judged at the moment it starts: a backup that overruns should keep
            // its friendly explanation rather than flipping to alarming mid-way.
            planned: inBackupWindow(),
        });
    },

    markUp: () => {
        if (get().online) return;
        set({ online: true, since: null, lastError: null, planned: false });
    },
}));
